import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  /** @type {AbortController} */
  #buttonAbortController = new AbortController();

  #isOpening = false;

  get productPageUrl() {
    /*
     * First try the normal Horizon product-card method.
     */
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest('product-card')
      );

    const productLink =
      productCard?.getProductCardLink?.();

    /*
     * If the theme method is unavailable, use the actual
     * product-card link directly.
     */
    if (productLink?.href) {
      const url = new URL(
        productLink.href,
        window.location.origin
      );

      const selectedVariantId =
        this.#getSelectedVariantId();

      if (
        selectedVariantId &&
        !url.searchParams.has('variant')
      ) {
        url.searchParams.set(
          'variant',
          selectedVariantId
        );
      }

      return url.toString();
    }

    /*
     * Fallback: find any product link inside the card.
     */
    const fallbackLink =
      this.closest('product-card')?.querySelector(
        'a.product-card__link[href]'
      );

    if (fallbackLink instanceof HTMLAnchorElement) {
      const url = new URL(
        fallbackLink.href,
        window.location.origin
      );

      const selectedVariantId =
        this.#getSelectedVariantId();

      if (selectedVariantId) {
        url.searchParams.set(
          'variant',
          selectedVariantId
        );
      }

      return url.toString();
    }

    return '';
  }

  /**
   * Gets the currently selected variant ID.
   */
  #getSelectedVariantId() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest('product-card')
      );

    return (
      productCard?.getSelectedVariantId?.() ||
      null
    );
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener(
      'change',
      this.#closeQuickAddModal
    );

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
      {
        signal:
          this.#cartUpdateAbortController.signal,
      }
    );

    /*
     * Direct listener for our magnifying glass.
     */
    const chooseButton =
      this.querySelector(
        '.quick-add__button--choose'
      );

    if (chooseButton) {
      chooseButton.addEventListener(
        'click',
        this.#handleDirectClick,
        {
          signal:
            this.#buttonAbortController.signal,
        }
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener(
      'change',
      this.#closeQuickAddModal
    );

    this.#abortController?.abort();

    this.#cartUpdateAbortController.abort();

    this.#buttonAbortController.abort();
  }

  /**
   * Cart update clears cached product content.
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Direct click from the magnifying glass.
   */
  #handleDirectClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#isOpening) {
      return;
    }

    this.#isOpening = true;

    try {
      await this.#openQuickView();
    } catch (error) {
      console.error(
        '[YEI QUICK VIEW]',
        error
      );
    } finally {
      this.#isOpening = false;
    }
  };

  /**
   * Horizon's original click handler.
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (this.#isOpening) {
      return;
    }

    this.#isOpening = true;

    try {
      await this.#openQuickView();
    } catch (error) {
      console.error(
        '[YEI QUICK VIEW]',
        error
      );
    } finally {
      this.#isOpening = false;
    }
  };

  /**
   * Main Quick View system.
   */
  async #openQuickView() {
    const productUrl =
      this.productPageUrl;

    if (!productUrl) {
      console.error(
        '[YEI QUICK VIEW] No se ha encontrado la URL del producto.'
      );

      return;
    }

    const modal =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(modal instanceof QuickAddDialog)
    ) {
      console.error(
        '[YEI QUICK VIEW] No existe #quick-add-dialog en la página.'
      );

      return;
    }

    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (!modalContent) {
      console.error(
        '[YEI QUICK VIEW] No existe #quick-add-modal-content.'
      );

      return;
    }

    /*
     * Try cache first.
     */
    let productGrid =
      this.#cachedContent.get(
        productUrl
      );

    /*
     * If not cached, download product page.
     */
    if (!productGrid) {
      const html =
        await this.fetchProductPage(
          productUrl
        );

      if (!html) {
        return;
      }

      const gridElement =
        html.querySelector(
          '[data-product-grid-content]'
        );

      if (!gridElement) {
        console.error(
          '[YEI QUICK VIEW] No se encontró [data-product-grid-content].'
        );

        return;
      }

      productGrid =
        /** @type {Element} */ (
          gridElement.cloneNode(true)
        );

      this.#cachedContent.set(
        productUrl,
        productGrid
      );
    }

    /*
     * Always use a fresh clone.
     */
    const freshContent =
      /** @type {Element} */ (
        productGrid.cloneNode(true)
      );

    /*
     * Build the modal content.
     */
    await this.updateQuickAddModal(
      freshContent
    );

    /*
     * Open the existing Horizon modal.
     */
    this.#openQuickAddModal();
  };

  /**
   * Updates the variant picker inside the modal.
   */
  #updateVariantPicker(
    newHtml
  ) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (!modalContent) {
      return;
    }

    const variantPicker =
      /** @type {VariantPicker} */ (
        modalContent.querySelector(
          'variant-picker'
        )
      );

    if (
      variantPicker &&
      typeof variantPicker.updateVariantPicker ===
        'function'
    ) {
      variantPicker.updateVariantPicker(
        newHtml
      );
    }
  }

  /**
   * Reset modal scroll.
   */
  #resetScroll() {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      return;
    }

    const productDetails =
      dialogComponent.querySelector(
        '.product-details'
      );

    const productMedia =
      dialogComponent.querySelector(
        '.product-information__media'
      );

    productDetails?.scrollTo({
      top: 0,
      behavior: 'instant',
    });

    productMedia?.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }

  /**
   * Keeps the button visible while modal is open.
   */
  #stayVisibleUntilDialogCloses(
    dialogComponent
  ) {
    this.toggleAttribute(
      'stay-visible',
      true
    );

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute(
          'stay-visible',
          false
        );
      },
      {
        once: true,
      }
    );
  }

  /**
   * Opens Horizon's existing Quick Add dialog.
   */
  #openQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      console.error(
        '[YEI QUICK VIEW] quick-add-dialog no es QuickAddDialog.'
      );

      return;
    }

    this.#stayVisibleUntilDialogCloses(
      dialogComponent
    );

    dialogComponent.showDialog();

    const dialog =
      dialogComponent.refs?.dialog;

    if (!dialog) {
      return;
    }

    dialog.addEventListener(
      'animationstart',
      this.#resetScroll.bind(this),
      {
        once: true,
      }
    );
  };

  /**
   * Closes Quick View.
   */
  #closeQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      return;
    }

    dialogComponent.closeDialog();
  };

  /**
   * Fetch product page.
   */
  async fetchProductPage(
    productPageUrl
  ) {
    if (!productPageUrl) {
      return null;
    }

    this.#abortController?.abort();

    this.#abortController =
      new AbortController();

    try {
      const response =
        await fetch(
          productPageUrl,
          {
            signal:
              this.#abortController.signal,
          }
        );

      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status}`
        );
      }

      const responseText =
        await response.text();

      return new DOMParser().parseFromString(
        responseText,
        'text/html'
      );
    } catch (error) {
      if (
        error instanceof Error &&
        error.name === 'AbortError'
      ) {
        return null;
      }

      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Builds the content shown inside Quick View.
   */
  async updateQuickAddModal(
    productGrid
  ) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (
      !productGrid ||
      !modalContent
    ) {
      return;
    }

    if (isMobileBreakpoint()) {
      const productDetails =
        productGrid.querySelector(
          '.product-details'
        );

      const productFormComponent =
        productGrid.querySelector(
          'product-form-component'
        );

      const variantPicker =
        productGrid.querySelector(
          'variant-picker'
        );

      const productPrice =
        productGrid.querySelector(
          'product-price'
        );

      const productTitle =
        document.createElement('a');

      productTitle.textContent =
        this.dataset.productTitle || '';

      productTitle.href =
        this.productPageUrl;

      const productHeader =
        document.createElement('div');

      productHeader.classList.add(
        'product-header'
      );

      productHeader.appendChild(
        productTitle
      );

      if (productPrice) {
        productHeader.appendChild(
          productPrice
        );
      }

      productGrid.appendChild(
        productHeader
      );

      if (variantPicker) {
        productGrid.appendChild(
          variantPicker
        );
      }

      if (productFormComponent) {
        productGrid.appendChild(
          productFormComponent
        );
      }

      productDetails?.remove();
    }

    /*
     * Insert product into the existing modal.
     */
    morph(
      modalContent,
      productGrid
    );

    /*
     * Sync selected variant.
     */
    this.#syncVariantSelection(
      modalContent
    );
  }

  /**
   * Updates button state.
   */
  #updateQuickAddButtonState = (
    event
  ) => {
    if (
      !(event.target instanceof HTMLElement)
    ) {
      return;
    }

    if (
      event.target.closest(
        'product-card'
      ) !==
      this.closest(
        'product-card'
      )
    ) {
      return;
    }

    const productOptionsCount =
      this.dataset.productOptionsCount;

    this.setAttribute(
      'data-quick-add-button',
      productOptionsCount === '1'
        ? 'add'
        : 'choose'
    );
  };

  /**
   * Sync variant selection.
   */
  #syncVariantSelection(
    modalContent
  ) {
    const selectedVariantId =
      this.#getSelectedVariantId();

    if (!selectedVariantId) {
      return;
    }

    const modalInputs =
      modalContent.querySelectorAll(
        'input[type="radio"][data-variant-id]'
      );

    for (
      const input of modalInputs
    ) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.variantId ===
          selectedVariantId &&
        !input.checked
      ) {
        input.checked = true;

        input.dispatchEvent(
          new Event(
            'change',
            {
              bubbles: true,
            }
          )
        );

        break;
      }
    }
  }
}

if (
  !customElements.get(
    'quick-add-component'
  )
) {
  customElements.define(
    'quick-add-component',
    QuickAddComponent
  );
}


/* ============================================================
   QUICK ADD DIALOG
   ============================================================ */

class QuickAddDialog
  extends DialogComponent {

  #abortController =
    new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      ThemeEvents.cartUpdate,
      this.handleCartUpdate,
      {
        signal:
          this.#abortController.signal,
      }
    );

    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#updateProductTitleLink
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  /**
   * Close after cart update.
   */
  handleCartUpdate = (
    event
  ) => {
    if (
      event.detail.data.didError
    ) {
      return;
    }

    this.closeDialog();
  };

  /**
   * Updates product title links.
   */
  #updateProductTitleLink = (
    event
  ) => {
    const anchorElement =
      /** @type {HTMLAnchorElement} */ (
        event.detail.data.html?.querySelector(
          '.view-product-title a'
        )
      );

    const viewMoreDetailsLink =
      /** @type {HTMLAnchorElement} */ (
        this.querySelector(
          '.view-product-title a'
        )
      );

    const mobileProductTitle =
      /** @type {HTMLAnchorElement} */ (
        this.querySelector(
          '.product-header a'
        )
      );

    if (!anchorElement) {
      return;
    }

    if (viewMoreDetailsLink) {
      viewMoreDetailsLink.href =
        anchorElement.href;
    }

    if (mobileProductTitle) {
      mobileProductTitle.href =
        anchorElement.href;
    }
  };

  /**
   * iOS compatibility.
   */
  #handleDialogClose = () => {
    const iosVersion =
      getIOSVersion();

    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (
        iosVersion.major === 16 &&
        iosVersion.minor >= 4
      )
    ) {
      return;
    }

    requestAnimationFrame(() => {
      const grid =
        document.querySelector(
          '#ResultsList [product-grid-view]'
        );

      if (!grid) {
        return;
      }

      const currentWidth =
        grid.getBoundingClientRect()
          .width;

      grid.style.width =
        `${currentWidth - 1}px`;

      requestAnimationFrame(() => {
        grid.style.width = '';
      });
    });
  };
}

if (
  !customElements.get(
    'quick-add-dialog'
  )
) {
  customElements.define(
    'quick-add-dialog',
    QuickAddDialog
  );
}